// =============================================================================
// features/sales/campaigns/useCampaignFilters.ts — Estado dos filtros de
// /campanhas serializado na URL (F35-S07 DoD: "Filtros refletidos na URL").
// Mesmo padrão de features/relatorios/hooks/useReportFilters.ts.
//
// Estado serializado em searchParams para deep-link e reload-safe:
//   ?status=active&status=paused&productId=uuid&mine=true&createdFrom=2026-08-01
//
// Defaults: sem filtro de status/produto, `mine=false` (todas as campanhas
// visíveis ao ator — a API já restringe sozinha quem não tem
// `sales_campaigns:manage` ao próprio, ver sales-campaigns.ts).
// =============================================================================

import type { CampaignStatus } from '@elemento/shared-schemas';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

export interface CampaignFiltersState {
  status: CampaignStatus[];
  productId: string | null;
  /** true = só campanhas do próprio ator (ownerUserId=userId). */
  mine: boolean;
  /** Início do período de criação (YYYY-MM-DD). '' = sem filtro. */
  createdFrom: string;
  /** Fim do período de criação (YYYY-MM-DD). '' = sem filtro. */
  createdTo: string;
  offset: number;
}

export interface CampaignFiltersActions {
  setStatus: (status: CampaignStatus[]) => void;
  setProductId: (id: string | null) => void;
  setMine: (mine: boolean) => void;
  setCreatedRange: (from: string, to: string) => void;
  setOffset: (offset: number) => void;
  clearAll: () => void;
}

const VALID_STATUSES = new Set<CampaignStatus>([
  'draft',
  'ready',
  'active',
  'paused',
  'completed',
  'cancelled',
]);

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function useCampaignFilters(): CampaignFiltersState & CampaignFiltersActions {
  const [params, setParams] = useSearchParams();

  const status = React.useMemo(
    () =>
      params
        .getAll('status')
        .filter((s): s is CampaignStatus => VALID_STATUSES.has(s as CampaignStatus)),
    [params],
  );

  const productId = params.get('productId');

  const mine = params.get('mine') === 'true';

  const readDay = (key: string): string => {
    const v = params.get(key);
    return v !== null && DAY_PATTERN.test(v) ? v : '';
  };
  const createdFrom = readDay('createdFrom');
  const createdTo = readDay('createdTo');

  const offset = (() => {
    const v = params.get('offset');
    const n = v !== null ? Number(v) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  })();

  const setStatus = React.useCallback(
    (next: CampaignStatus[]) => {
      setParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete('status');
        for (const s of next) p.append('status', s);
        p.delete('offset');
        return p;
      });
    },
    [setParams],
  );

  const setProductId = React.useCallback(
    (id: string | null) => {
      setParams((prev) => {
        const p = new URLSearchParams(prev);
        if (id === null) p.delete('productId');
        else p.set('productId', id);
        p.delete('offset');
        return p;
      });
    },
    [setParams],
  );

  const setMine = React.useCallback(
    (next: boolean) => {
      setParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next) p.set('mine', 'true');
        else p.delete('mine');
        p.delete('offset');
        return p;
      });
    },
    [setParams],
  );

  const setCreatedRange = React.useCallback(
    (from: string, to: string) => {
      setParams((prev) => {
        const p = new URLSearchParams(prev);
        if (from === '') p.delete('createdFrom');
        else p.set('createdFrom', from);
        if (to === '') p.delete('createdTo');
        else p.set('createdTo', to);
        p.delete('offset');
        return p;
      });
    },
    [setParams],
  );

  const setOffset = React.useCallback(
    (next: number) => {
      setParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next <= 0) p.delete('offset');
        else p.set('offset', String(next));
        return p;
      });
    },
    [setParams],
  );

  const clearAll = React.useCallback(() => {
    setParams(new URLSearchParams());
  }, [setParams]);

  return {
    status,
    productId,
    mine,
    createdFrom,
    createdTo,
    offset,
    setStatus,
    setProductId,
    setMine,
    setCreatedRange,
    setOffset,
    clearAll,
  };
}
